// src/pages/PrintBorrowForm.jsx
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { API_BASE_URL } from "../utils/config";
const MAX_ROWS = 10;

function useQuery() {
  const location = useLocation();
  return new URLSearchParams(location.search);
}

function formatIDDate(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function PrintBorrowForm() {
  const query = useQuery();

  // legacy mode (old system)
  const idsParam = query.get("ids") || "";

  // new mode (borrow batch)
  const batchParam = query.get("batch") || "";

  const [batch, setBatch] = useState(null);
  const [requests, setRequests] = useState([]); // legacy only
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("accessToken");

  useEffect(() => {
    async function fetchAll() {
      setError("");
      setLoading(true);

      try {
        if (!token) throw new Error("Not logged in.");

        // -------- NEW: Batch printing (direct fetch) --------
        if (batchParam) {
          const res = await fetch(
            `${API_BASE_URL}/api/borrow-batches/${batchParam}/`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (!res.ok) {
            if (res.status === 404) throw new Error("Batch not found.");
            if (res.status === 403) throw new Error("Forbidden.");
            throw new Error("Failed to load batch.");
          }

          const found = await res.json();
          setBatch(found);
          setRequests([]);
          return;
        }

        // -------- LEGACY: ids printing --------
        const ids = idsParam
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .slice(0, 10);

        if (!ids.length) throw new Error("No batch selected.");

        const results = [];
        for (const id of ids) {
          const res = await fetch(`${API_BASE_URL}/api/borrow-requests/${id}/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) throw new Error("Failed to load one or more requests.");
          results.push(await res.json());
        }

        setRequests(results);
        setBatch(null);
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load print data.");
      } finally {
        setLoading(false);
      }
    }

    fetchAll();
  }, [idsParam, batchParam, token]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <p className="text-sm text-slate-600">Loading form…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  // ---------- NEW: batch-based rendering ----------
  if (batch) {
    const borrowerName = batch.user || "-";
    const eventName = batch.event_name || "-";
    const eventPic = batch.event_pic_name || "-";
    const eventDate = formatIDDate(batch.event_date) || "-";
    const borrowDate = formatIDDate(batch.borrow_date || batch.created_at) || "-";
    const expectedReturn = formatIDDate(batch.expected_return_date) || "-";
    const items = batch.items || [];
    const extraRows = Math.max(0, MAX_ROWS - items.length);

    return (
      <div className="print-wrapper min-h-screen bg-slate-100 flex items-center justify-center py-10">
        <div
          id="print-area"
          className="bg-white w-full max-w-4xl border border-slate-300 shadow-sm p-6 print:shadow-none print:border-0 print:p-0 print:max-w-none"
        >
          <div className="absolute top-4 left-4 text-xl font-bold hidden print:block">
            {batch.reference_code || `BB-${batch.id}`}
          </div>

          <h1 className="text-center text-xl font-bold mb-6 hidden print:block">
            FORMULIR
            <br />
            PEMINJAMAN & PENGEMBALIAN BARANG
          </h1>

          <div className="flex justify-between items-center mb-4 print:hidden">
            <h1 className="text-lg font-semibold">
              Formulir Peminjaman &amp; Pengembalian Barang
            </h1>
            <button
              type="button"
              onClick={handlePrint}
              className="no-print inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-800 text-white hover:bg-slate-900"
            >
              Print
            </button>
          </div>

          <div className="text-xs mb-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="mb-1">
                  <span className="font-semibold">Nama Peminjam</span>:{" "}
                  <span className="inline-block min-w-[160px] border-b border-slate-400">
                    {borrowerName}
                  </span>
                </p>

                <p className="mb-1">
                  <span className="font-semibold">Nama Event</span>:{" "}
                  <span className="inline-block min-w-[160px] max-w-[340px] border-b border-slate-400 whitespace-nowrap overflow-hidden text-ellipsis align-bottom">
                    {eventName}
                  </span>
                </p>

                <p className="mb-1">
                  <span className="font-semibold">Tanggal Event</span>:{" "}
                  <span className="inline-block min-w-[120px] border-b border-slate-400">{eventDate}</span>
                </p>

                <p className="mb-1">
                  <span className="font-semibold">PIC Event</span>:{" "}
                  <span className="inline-block min-w-[160px] border-b border-slate-400">{eventPic}</span>
                </p>
              </div>

              <div className="text-right">
                <p className="mb-1">
                  <span className="font-semibold">Tanggal Peminjaman</span>:{" "}
                  <span className="inline-block min-w-[120px] border-b border-slate-400">
                    {borrowDate}
                  </span>
                </p>
                <p className="mb-1">
                  <span className="font-semibold">Rencana Pengembalian</span>:{" "}
                  <span className="inline-block min-w-[120px] border-b border-slate-400">
                    {expectedReturn}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <table className="w-full table-fixed text-[12px] border border-slate-400 border-collapse">
            <thead>
              <tr className="bg-slate-100">
                <th className="border border-slate-400 px-2 py-1 w-[8%]">No</th>
                <th className="border border-slate-400 px-2 py-1 w-[42%]">Nama Barang</th>
                <th className="border border-slate-400 px-2 py-1 w-[16%]">Jumlah Dipinjam</th>
                <th className="border border-slate-400 px-2 py-1 w-[16%]">Jumlah Dikembalikan</th>
                <th className="border border-slate-400 px-2 py-1 w-[18%]">Keterangan</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, index) => (
                <tr key={`${it.item_id || it.item || "row"}-${index}`} className="h-7">
                  <td className="border border-slate-400 px-2 py-1 text-center">
                    {index + 1}
                  </td>
                  <td className="border border-slate-400 px-2 py-1">
                    {it.item_name || "-"}
                  </td>
                  <td className="border border-slate-400 px-2 py-1 text-center">
                    {it.quantity}
                  </td>
                  <td className="border border-slate-400 px-2 py-1">&nbsp;</td>
                  <td className="border border-slate-400 px-2 py-1">&nbsp;</td>
                </tr>
              ))}

              {Array.from({ length: extraRows }).map((_, idx) => (
                <tr key={`blank-${idx}`} className="h-7">
                  <td className="border border-slate-400 px-2 py-1">&nbsp;</td>
                  <td className="border border-slate-400 px-2 py-1">&nbsp;</td>
                  <td className="border border-slate-400 px-2 py-1">&nbsp;</td>
                  <td className="border border-slate-400 px-2 py-1">&nbsp;</td>
                  <td className="border border-slate-400 px-2 py-1">&nbsp;</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-6 text-[12px]">
            <div className="w-[90%] mx-auto">
              <div className="flex justify-between mb-3">
                <span className="font-semibold">Tanggal Peminjaman:</span>
                <span className="font-semibold mb-2">Tanggal Kembali:</span>
                <span></span>
              </div>

              <div className="grid grid-cols-2 gap-12">
                <div className="flex flex-col items-center gap-4">
                  <div className="flex gap-8">
                    <div className="text-center">
                      <p className="mb-14">Kepala Logistik</p>
                      <div className="border-t border-slate-400 w-24" />
                    </div>
                    <div className="text-center">
                      <p className="mb-14">Yang Menyerahkan</p>
                      <div className="border-t border-slate-400 w-24" />
                    </div>
                    <div className="text-center">
                      <p className="mb-14">Penerima</p>
                      <div className="border-t border-slate-400 w-24" />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-center gap-4">
                  <div className="flex gap-8">
                    <div className="text-center">
                      <p className="mb-14">Kepala Logistik</p>
                      <div className="border-t border-slate-400 w-24" />
                    </div>
                    <div className="text-center">
                      <p className="mb-14">Diterima Oleh</p>
                      <div className="border-t border-slate-400 w-24" />
                    </div>
                    <div className="text-center">
                      <p className="mb-14">Yang Mengembalikan</p>
                      <div className="border-t border-slate-400 w-24" />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    );
  }

  // legacy fallback
  if (!requests.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <p className="text-sm text-red-600">No data.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <p className="text-sm text-slate-600">
        Legacy print mode still exists, but you should use batch printing now.
      </p>
    </div>
  );
}
